/**
 * Pass standard color-picker swatches (e.g. tardy detention).
 * @type {ReadonlyArray<{ label: string, hex: string }>}
 */
const DEFAULT_COLOR_PICKER_PALETTE = Object.freeze([
  { label: "Color1", hex: "#B8D8F8" },
  { label: "Color2", hex: "#7986CB" },
  { label: "Color3", hex: "#FFC8B7" },
  { label: "Color4", hex: "#567593" },
  { label: "Color5", hex: "#33B679" },
  { label: "Color6", hex: "#F6C026" },
  { label: "Color7", hex: "#039BE5" },
  { label: "Color8", hex: "#A19AF5" },
  { label: "Color9", hex: "#E67C73" },
  { label: "Color10", hex: "#616161" },
  { label: "Color12", hex: "#008566" },
  { label: "Color13", hex: "#F5511D" },
  { label: "Color14", hex: "#3F51B5" },
  { label: "Color15", hex: "#8E24AA" },
  { label: "Color16", hex: "#D60000" }
])

/**
 * Core value category colors — Figma Pass Main File, Create/Edit Core Value modal
 * (node 3166:130214). `label` is the API `category` code (POST/GET); `hex` is the UI swatch.
 * @type {ReadonlyArray<{ label: string, hex: string }>}
 */
const CORE_VALUE_COLOR_PALETTE = Object.freeze([
  { label: "W", hex: "#338FEB" },
  { label: "P", hex: "#8783F7" },
  { label: "T", hex: "#379DAB" },
  { label: "R", hex: "#D061CC" },
  { label: "O", hex: "#C87012" },
  { label: "G", hex: "#339D85" },
  { label: "L", hex: "#9E9100" },
  { label: "S", hex: "#EA614D" },
  { label: "C", hex: "#7891A9" }
])

const normalizePaletteHex = (hex) =>
  String(hex ?? "")
    .trim()
    .toUpperCase()

const CORE_VALUE_HEX_BY_CATEGORY = Object.freeze(
  Object.fromEntries(
    CORE_VALUE_COLOR_PALETTE.map(({ label, hex }) => [label, hex])
  )
)

const CORE_VALUE_CATEGORY_BY_HEX = Object.freeze(
  Object.fromEntries(
    CORE_VALUE_COLOR_PALETTE.map(({ label, hex }) => [
      normalizePaletteHex(hex),
      label
    ])
  )
)

/**
 * @param {string} hex
 * @param {ReadonlyArray<{ hex?: string }>} palette
 * @returns {boolean}
 */
const isHexInColorPalette = (hex, palette) => {
  const normalized = normalizePaletteHex(hex)
  if (!normalized) return false
  return (palette || []).some(
    (entry) => normalizePaletteHex(entry?.hex) === normalized
  )
}

/**
 * @param {string} category API category code (e.g. "W") or legacy hex.
 * @returns {string} Hex for UI binding, or empty string when unknown.
 */
const resolveCoreValueCategoryHex = (category) => {
  const value = String(category ?? "").trim()
  if (!value) return ""
  if (value.startsWith("#")) return value
  return CORE_VALUE_HEX_BY_CATEGORY[value.toUpperCase()] || ""
}

/**
 * @param {string} hex Palette hex from the color picker.
 * @returns {string} API category code (e.g. "W"), or empty string when unknown.
 */
const resolveCoreValueCategoryCode = (hex) => {
  const normalized = normalizePaletteHex(hex)
  if (!normalized) return ""
  return CORE_VALUE_CATEGORY_BY_HEX[normalized] || ""
}

export default {
  DEFAULT_COLOR_PICKER_PALETTE,
  CORE_VALUE_COLOR_PALETTE,
  CORE_VALUE_HEX_BY_CATEGORY,
  CORE_VALUE_CATEGORY_BY_HEX,
  normalizePaletteHex,
  isHexInColorPalette,
  resolveCoreValueCategoryHex,
  resolveCoreValueCategoryCode
}

export {
  DEFAULT_COLOR_PICKER_PALETTE,
  CORE_VALUE_COLOR_PALETTE,
  CORE_VALUE_HEX_BY_CATEGORY,
  CORE_VALUE_CATEGORY_BY_HEX,
  normalizePaletteHex,
  isHexInColorPalette,
  resolveCoreValueCategoryHex,
  resolveCoreValueCategoryCode
}
